import React, { useMemo } from 'react';
import { AvailabilityStatusLabel } from '@/components/availability-status-label';
import { ItemListOptionMetadata } from '@/contexts/item-context-types';
import { PeakPriceShield } from '@/components/orders/peak-price-shield';
import { PrebookLabel } from '@/components/prebook-label';
import { useShouldRenderAvailabilityStatusLabel } from './use-add-to-order-disabled';

export interface LabelConfig {
  key: string;
  render: () => React.ReactNode;
}

export const useRenderedLabels = (labels?: LabelConfig[]) => {
  return useMemo(() => {
    return labels?.map((label) => (
      <React.Fragment key={label.key}>{label.render()}</React.Fragment>
    ));
  }, [labels]);
};

export const useItemLabels = (
  metadata: ItemListOptionMetadata,
  isSalesRepPeakPriceProtectionInEffect = false,
  compact = false,
): LabelConfig[] => {
  const { availabilityStatus, prebookConfig, availableQuantity } = metadata;
  const shouldRenderAvailabilityStatusLabel =
    useShouldRenderAvailabilityStatusLabel({
      availabilityStatus,
      availableQuantity,
    });

  return useMemo(() => {
    const labels = [];
    if (Boolean(prebookConfig)) {
      labels.push({
        key: 'prebook-label',
        render: () => (
          <PrebookLabel compact={compact} prebookConfig={prebookConfig!} />
        ),
      });
    }

    if (!Boolean(prebookConfig) && shouldRenderAvailabilityStatusLabel) {
      labels.push({
        key: 'availability-status-label',
        render: () => (
          <AvailabilityStatusLabel
            availabilityStatus={availabilityStatus}
            compact={compact}
          />
        ),
      });
    }
    if (isSalesRepPeakPriceProtectionInEffect) {
      labels.push({
        key: 'peak-price-label',
        render: () => <PeakPriceShield key="peak-price-shield" />,
      });
    }
    return labels;
  }, [
    availabilityStatus,
    compact,
    isSalesRepPeakPriceProtectionInEffect,
    prebookConfig,
    shouldRenderAvailabilityStatusLabel,
  ]);
};
